"use client";

import { useCallback, useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { Controller, useForm } from "react-hook-form";

import { Column, Row } from "@/components/containers";
import { Descriptions } from "@/components/displays";
import { Input, ButtonRound } from "@/components/inputs";
import { GLOBAL_LINKS } from "@/configs/links";
import { usePostPosaCheck } from "@/generated/open-api/posa/posa";

import { PosaChargeInputSchema, PosaChargeInputSchemaValue } from "./schema";

export interface PosaChargeInputProps {
  setPosaCardNumber: (valid: string) => void;
  setPosaCardChargePoint: (point: number) => void;
}

export function PosaChargeInput({
  setPosaCardNumber,
  setPosaCardChargePoint,
}: PosaChargeInputProps) {
  const [serverErrorMessage, setServerErrorMessage] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm<PosaChargeInputSchemaValue>({
    resolver: zodResolver(PosaChargeInputSchema),
    defaultValues: {
      code: "",
    },
    mode: "onChange",
  });
  const { mutateAsync: postPosaCheck, data, error, isPending } = usePostPosaCheck();

  const onSubmit = useCallback(
    async (formValues: PosaChargeInputSchemaValue) => {
      await postPosaCheck({ data: { cardType: "POINT_CHARGE", cardNumber: formValues.code } });
    },
    [postPosaCheck]
  );

  useEffect(() => {
    if (data?.chargePoint) {
      setPosaCardNumber(getValues("code"));
      setPosaCardChargePoint(data.chargePoint);
    }
  }, [data?.chargePoint, getValues, setPosaCardChargePoint, setPosaCardNumber]);

  useEffect(() => {
    if (error?.response?.data.message) {
      setServerErrorMessage(error.response.data.message);
    }
  }, [error?.response?.data.message]);

  // 入力が変更されたときにサーバーエラーメッセージをクリアする
  useEffect(() => {
    const subscription = watch((_value, { name }) => {
      if (name === "code" && serverErrorMessage) {
        setServerErrorMessage(undefined);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, serverErrorMessage]);

  return (
    <Column align="center" gap={20}>
      <Row align="center" justify="center">
        <b className="relative leading-[28px]">
          <p className="m-0">POSAカード裏面の</p>
          <p className="m-0">コード番号を入力してください</p>
        </b>
      </Row>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <Column gap={20}>
          <Column gap={10}>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <Input autoFocus placeholder="コード番号" maxLength={10} {...field} />
              )}
            />
            {(errors.code || serverErrorMessage) && (
              <p className="text-message-error text-xs text-left">
                {errors.code?.message || serverErrorMessage}
              </p>
            )}
          </Column>
          <ButtonRound
            label="次に進む"
            type="submit"
            className="m-auto"
            disabled={!isValid || isSubmitting || isPending}
          />
        </Column>
      </form>
      <Row align="center" justify="center" className="text-[12px]">
        <Link
          href="/mypage"
          className="[text-decoration:underline] relative leading-[16px] text-[inherit] cursor-pointer"
        >
          マイページに戻る
        </Link>
      </Row>
      {error && (
        <Column
          gap={20}
          className="w-full border-[#c8c8c8] border-solid border-[1px] rounded-[10px] p-[20px]"
        >
          <b className="text-[20px]">コードが確認できない場合</b>
          <div className="text-left text-[16px]">
            <Descriptions
              descriptions={[
                "コード番号に誤りがないかご確認ください（英数字の打ち間違いや桁数など）",
                "コードは一度のみ使用可能で、2回以上の使用はできません。",
                "購入から6ヶ月を過ぎたコードは無効となり、使用できません。",
              ]}
            />
          </div>
          <p className="text-left text-[16px]">
            上記で解決されない場合は、弊社
            <a
              href={GLOBAL_LINKS.zendesk}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary"
            >
              お問合せフォーム
            </a>
            からお問合せください。
          </p>
        </Column>
      )}
    </Column>
  );
}
